export interface WorkOrder {
  id: string;
  product: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD';
  progress: number;
  plannedQty: number;
  producedQty: number;
  defectQty: number;
  line: string;
  startTime: string;
}

export interface InventoryItem {
  sku: string;
  description: string;
  location: string;
  physicalQty: number;
  reservedQty: number;
  unit: string;
}

export interface MachineState {
  id: string;
  name: string;
  status: 'RUNNING' | 'PAUSED' | 'ERROR' | 'IDLE' | 'MAINTENANCE';
  currentOperator: string;
  oee: number;
  line: string;
}

export interface Alert {
  id: string;
  severity: 'CRITICAL' | 'WARNING' | 'INFO';
  message: string;
  source: string;
  timestamp: string;
  acknowledged: boolean;
}

export interface MESEvent {
  id: string;
  timestamp: string;
  type: string;
  station: string;
  user: string;
  status: 'OK' | 'WARNING' | 'ERROR' | 'INFO';
  detail: string;
}

export const initialWorkOrders: WorkOrder[] = [
  { id: 'WO-2024-0892', product: 'Aluminum Chassis CX-200', status: 'IN_PROGRESS', progress: 68, plannedQty: 500, producedQty: 340, defectQty: 12, line: 'Line A', startTime: '2024-03-15T06:00:00' },
  { id: 'WO-2024-0893', product: 'Solar Panel PS-400', status: 'IN_PROGRESS', progress: 42, plannedQty: 200, producedQty: 84, defectQty: 3, line: 'Line B', startTime: '2024-03-15T07:30:00' },
  { id: 'WO-2024-0894', product: 'Control Module MC-50', status: 'PENDING', progress: 0, plannedQty: 150, producedQty: 0, defectQty: 0, line: 'Line A', startTime: '2024-03-15T14:00:00' },
  { id: 'WO-2024-0895', product: 'Steel Frame BA-100', status: 'COMPLETED', progress: 100, plannedQty: 300, producedQty: 298, defectQty: 8, line: 'Line C', startTime: '2024-03-14T06:00:00' },
];

export const initialInventory: InventoryItem[] = [
  { sku: 'MAT-AL-6061', description: 'Aluminum 6061-T6 Sheet', location: 'WH-A-01', physicalQty: 1200, reservedQty: 500, unit: 'kg' },
  { sku: 'MAT-ST-304', description: 'Stainless Steel 304 Bar', location: 'WH-A-02', physicalQty: 800, reservedQty: 750, unit: 'kg' },
  { sku: 'CMP-PCB-01', description: 'Main Control PCB', location: 'WH-B-01', physicalQty: 340, reservedQty: 150, unit: 'pcs' },
  { sku: 'CMP-SNS-T1', description: 'Temperature Sensor T1', location: 'WH-B-02', physicalQty: 45, reservedQty: 40, unit: 'pcs' },
  { sku: 'PKG-BOX-L', description: 'Large Packaging Box', location: 'WH-C-01', physicalQty: 2000, reservedQty: 300, unit: 'pcs' },
];

export const initialMachines: MachineState[] = [
  { id: 'MCH-CNC-01', name: 'CNC Milling #1', status: 'RUNNING', currentOperator: 'Carlos Méndez', oee: 87.5, line: 'Line A' },
  { id: 'MCH-CNC-02', name: 'CNC Lathe #2', status: 'PAUSED', currentOperator: 'Ana García', oee: 72.3, line: 'Line A' },
  { id: 'MCH-LAS-01', name: 'Laser Cutter #1', status: 'RUNNING', currentOperator: 'Roberto Silva', oee: 91.2, line: 'Line B' },
  { id: 'MCH-ROB-01', name: 'Welding Robot #1', status: 'ERROR', currentOperator: 'System', oee: 0, line: 'Line C' },
  { id: 'MCH-INS-01', name: 'AI Visual Inspection', status: 'RUNNING', currentOperator: 'EXPAI Vision', oee: 95.8, line: 'Line A' },
];

export const initialAlerts: Alert[] = [
  { id: 'ALT-001', severity: 'CRITICAL', message: 'Welding Robot #1 — Z-axis servo failure', source: 'MCH-ROB-01', timestamp: '2024-03-15T10:23:00', acknowledged: false },
  { id: 'ALT-002', severity: 'WARNING', message: 'Low stock: Temperature Sensor T1 (5 units available)', source: 'INV-CMP-SNS-T1', timestamp: '2024-03-15T09:45:00', acknowledged: false },
  { id: 'ALT-003', severity: 'WARNING', message: 'CNC Lathe #2 OEE below threshold (72.3%)', source: 'MCH-CNC-02', timestamp: '2024-03-15T08:30:00', acknowledged: true },
];

export const initialEvents: MESEvent[] = [
  { id: 'EVT-001', timestamp: '10:23:15', type: 'ALARM', station: 'Line C / ROB-01', user: 'System', status: 'ERROR', detail: 'Z-axis servo failure detected' },
  { id: 'EVT-002', timestamp: '10:15:42', type: 'PRODUCTION', station: 'Line A / CNC-01', user: 'C. Méndez', status: 'OK', detail: 'Batch #340 completed — WO-0892' },
  { id: 'EVT-003', timestamp: '10:12:08', type: 'QUALITY', station: 'Line A / INS-01', user: 'EXPAI Vision', status: 'WARNING', detail: 'Minor defect detected — Surface scratch' },
  { id: 'EVT-004', timestamp: '10:05:33', type: 'INVENTORY', station: 'WH-B-02', user: 'System', status: 'WARNING', detail: 'Minimum stock alert — CMP-SNS-T1' },
  { id: 'EVT-005', timestamp: '09:58:11', type: 'PRODUCTION', station: 'Line B / LAS-01', user: 'R. Silva', status: 'OK', detail: 'Setup completed — WO-0893' },
  { id: 'EVT-006', timestamp: '09:45:27', type: 'MAINTENANCE', station: 'Line A / CNC-02', user: 'A. García', status: 'INFO', detail: 'Scheduled pause — Tool change' },
  { id: 'EVT-007', timestamp: '09:30:00', type: 'PRODUCTION', station: 'Line A / CNC-01', user: 'C. Méndez', status: 'OK', detail: 'Batch #339 completed — WO-0892' },
  { id: 'EVT-008', timestamp: '09:15:44', type: 'QUALITY', station: 'Line A / INS-01', user: 'EXPAI Vision', status: 'OK', detail: 'Inspection OK — Batch #338' },
];

export const productionChartData = [
  { hour: '06:00', produccion: 42, defectos: 1 },
  { hour: '07:00', produccion: 48, defectos: 2 },
  { hour: '08:00', produccion: 45, defectos: 1 },
  { hour: '09:00', produccion: 52, defectos: 3 },
  { hour: '10:00', produccion: 38, defectos: 2 },
  { hour: '11:00', produccion: 50, defectos: 1 },
  { hour: '12:00', produccion: 35, defectos: 4 },
  { hour: '13:00', produccion: 30, defectos: 1 },
];